
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Video } from '@/types';
import { formatDuration } from '@/lib/utils';
import ProcessingIndicator from '@/components/ProcessingIndicator';
import VideoProcessingStatus from '@/components/VideoProcessingStatus';
import { cn } from '@/lib/utils';
import { Play, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface VideoCardProps {
  video: Video;
  className?: string;
}

export default function VideoCard({ video, className }: VideoCardProps) {
  const [showProcessingStatus, setShowProcessingStatus] = useState(false);

  return (
    <div 
      className={cn(
        'rounded-lg overflow-hidden bg-white video-card-shadow transition-all duration-300 hover:-translate-y-1 hover:shadow-lg', 
        className
      )}
    >
      <Link to={`/videos/${video.id}`} className="block">
        <div className="relative group">
          <img 
            src={video.thumbnail} 
            alt={video.title} 
            className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
            <div className="rounded-full bg-white/80 p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-0 group-hover:scale-100">
              <Play className="h-5 w-5 text-video-blue" />
            </div>
          </div>
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
            {formatDuration(video.duration)}
          </div>
          {/* Indicator for real vs demo video */}
          {video.isRealVideo ? (
            <div className="absolute top-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded">
              Your Video
            </div>
          ) : (
            <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
              Demo
            </div>
          )}
        </div>
      </Link>
      
      <div className="p-4">
        <Link to={`/videos/${video.id}`}>
          <h3 className="font-medium text-lg mb-2 line-clamp-2 group-hover:text-video-blue transition-colors">{video.title}</h3>
        </Link>
        
        <div className="text-sm text-muted-foreground mb-3">
          {new Date(video.createdAt).toLocaleDateString()}
          {video.metadata && (
            <div className="mt-1 flex flex-wrap gap-1">
              {video.metadata.speakers.slice(0, 2).map((speaker, i) => (
                <span key={i} className="text-xs bg-accent px-1.5 py-0.5 rounded">
                  {speaker}
                </span>
              ))}
              {video.metadata.speakers.length > 2 && (
                <span className="text-xs bg-accent px-1.5 py-0.5 rounded">
                  +{video.metadata.speakers.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Show processing status for real videos */}
        {video.isRealVideo ? (
          <div className="space-y-2">
            <ProcessingIndicator 
              status={video.processingStatus} 
              progress={video.processingProgress} 
            />
            
            {/* Processing Status Toggle Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                setShowProcessingStatus(!showProcessingStatus);
              }}
              className="w-full"
            >
              <Info className="h-4 w-4 mr-2" />
              {showProcessingStatus ? 'Hide' : 'Show'} Processing Details
            </Button>
            
            {/* Detailed Processing Status */}
            {showProcessingStatus && (
              <div className="mt-3 bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-sm text-gray-700 mb-3">Processing Details</h4>
                <VideoProcessingStatus 
                  videoId={video.id}
                  onStatusChange={(status) => {
                    // Update the video status if needed
                    console.log('Video status changed:', status);
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <ProcessingIndicator 
            status={video.processingStatus} 
            progress={video.processingProgress} 
          />
        )}
      </div>
    </div>
  );
}
